/** 视频的宽高、时长与第 1 秒截帧（海报），由浏览器读取 */

export interface VideoInfo {
    width: number;
    height: number;
    /** 时长（秒，未取整）；读不到为 0 */
    durationSeconds: number;
}

const POSTER_SECOND = 1;
const POSTER_MAX_WIDTH = 1920;
const POSTER_QUALITY = 0.85;

const withVideo = <T>(file: Blob, work: (video: HTMLVideoElement) => Promise<T>): Promise<T> => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    return work(video).finally(() => {
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(url);
    });
};

const waitFor = (video: HTMLVideoElement, event: 'loadedmetadata' | 'seeked'): Promise<void> =>
    new Promise((resolve, reject) => {
        const cleanup = () => {
            video.removeEventListener(event, onDone);
            video.removeEventListener('error', onError);
        };
        const onDone = () => {
            cleanup();
            resolve();
        };
        const onError = () => {
            cleanup();
            reject(new Error('video decode failed'));
        };
        video.addEventListener(event, onDone);
        video.addEventListener('error', onError);
    });

export const readVideoInfo = (file: Blob): Promise<VideoInfo> => withVideo(file, async (video) => {
    await waitFor(video, 'loadedmetadata');
    return {
        width: video.videoWidth,
        height: video.videoHeight,
        durationSeconds: Number.isFinite(video.duration) ? video.duration : 0,
    };
});

/** 截取第 1 秒（不足 1 秒取开头）画面为 JPEG，作为默认海报；浏览器解不了该编码时返回 null */
export const captureVideoPoster = (file: Blob, fileName: string): Promise<File | null> => withVideo(file, async (video) => {
    try {
        await waitFor(video, 'loadedmetadata');
        video.currentTime = Math.min(POSTER_SECOND, Math.max(0, video.duration - 0.1));
        await waitFor(video, 'seeked');
        const width = Math.min(video.videoWidth, POSTER_MAX_WIDTH);
        const height = Math.round((video.videoHeight * width) / video.videoWidth);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')?.drawImage(video, 0, 0, width, height);
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', POSTER_QUALITY));
        if (!blob) return null;
        const baseName = fileName.replace(/\.[^.]+$/, '');
        return new File([blob], `${baseName}-poster.jpg`, { type: 'image/jpeg' });
    } catch {
        return null;
    }
});
